import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { useProfile } from '../context/ProfileContext';
import { callOpenAI } from '../openaiClient';

const { FiUser, FiLinkedin, FiGithub, FiGlobe, FiAlertCircle, FiCheckCircle, FiRefreshCw, FiZap, FiTarget, FiLightbulb, FiChevronRight } = FiIcons;

const ProfileOptimizer = () => {
  const { session } = useAuth();
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const applyHeadline = async (newHeadline) => {
    // Optimistic UI Update
    setProfileOptimizationData(prev => {
      const updatedSections = prev.sections.map(section => {
        if (section.id === 'headline') {
          return { ...section, current: newHeadline };
        }
        return section;
      });
      return { ...prev, sections: updatedSections };
    });

    const { error } = await supabase.from('profiles').update({ title: newHeadline }).eq('id', session.user.id);
    if (!error) {
      runAudit();
    }
  };

  const [profileOptimizationData, setProfileOptimizationData] = useState({ overallScore: 0, sections: [], tips: [] });
  const [isAuditing, setIsAuditing] = useState(false);
  const [showAllTips, setShowAllTips] = useState(false);

  const { data, cachedOptimizationData, setCachedOptimizationData } = useProfile();
  const profileData = data?.user;

  const runAudit = async () => {
    if (!profileData) return;
    setIsAuditing(true);

    try {
      const name = profileData.name || 'User';
      const title = profileData.title || 'Professional';
      const skills = profileData.skills || [];

      const systemPrompt = `You are a career profile optimizer. Analyze the given profile data and return a JSON object with this exact structure:
{
  "overallScore": integer (0-100),
  "headlineOptimized": "string (a better headline. If the Current Title is already highly optimized, return the identical Current Title as headlineOptimized)",
  "headlineOptimized": "string (a better headline)",
  "headlineSuggestions": ["string", "string", "string"],
  "marketScore": integer (0-100),
  "matchRate": integer (0-100),
  "topKeywords": ["string", "string", "string", "string", "string"],
  "tips": [
    { "id": 1, "category": "Impact", "impact": "High", "title": "string", "description": "string" }
  ] (Return exactly 5 to 7 highly actionable tips here)
}`;

      const userPrompt = `Profile Name: ${name}\nCurrent Title: ${title}\nSkills: ${skills.join(', ')}`;

      const aiResponse = await callOpenAI(systemPrompt, userPrompt);

      const newOptData = {
        overallScore: aiResponse.overallScore,
        sections: [
          {
            id: 'headline',
            name: 'Headline Optimization',
            score: aiResponse.overallScore > 80 ? 92 : 75,
            current: title,
            aiOptimized: aiResponse.headlineOptimized,
            suggestions: aiResponse.headlineSuggestions
          },
          {
            id: 'social',
            name: 'Web Presence',
            score: profileData.social_links && profileData.social_links.filter(l => l.url).length === 3 ? 100 : profileData.social_links && profileData.social_links.filter(l => l.url).length > 0 ? 60 : 30,
            links: profileData.social_links?.length ? profileData.social_links.map(link => ({
              platform: link.platform,
              url: link.url,
              status: link.url ? 'Optimized' : 'Missing'
            })) : [
              { platform: 'LinkedIn', url: '', status: 'Missing' },
              { platform: 'GitHub', url: '', status: 'Missing' },
              { platform: 'Portfolio', url: '', status: 'Missing' }
            ]
          },
          {
            id: 'market',
            name: 'Market Alignment',
            score: aiResponse.marketScore,
            matchRate: aiResponse.matchRate,
            topKeywords: aiResponse.topKeywords
          }
        ],
        tips: aiResponse.tips
      };
      setProfileOptimizationData(newOptData);
      setCachedOptimizationData(newOptData);
    } catch (error) {
      console.error("Audit failed:", error);
    } finally {
      setIsAuditing(false);
    }
  };

  useEffect(() => {
    if (cachedOptimizationData && profileOptimizationData.overallScore === 0) {
      setProfileOptimizationData(cachedOptimizationData);
    } else if (!cachedOptimizationData) {
      runAudit();
    }
  }, [profileData]);

  const { overallScore, sections, tips } = profileOptimizationData;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Optimized': return <SafeIcon icon={FiCheckCircle} className="text-green-500" />;
      case 'Needs Update': return <SafeIcon icon={FiRefreshCw} className="text-orange-500" />;
      case 'Missing': return <SafeIcon icon={FiAlertCircle} className="text-red-500" />;
      default: return null;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile Optimizer</h1>
          <p className="text-gray-500 mt-1">Enhance your professional identity for maximum ATS and recruiter visibility.</p>
        </div>
        <button
          onClick={runAudit}
          disabled={isAuditing}
          className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg ${
            isAuditing ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/25'
          }`}
        >
          <SafeIcon icon={FiZap} /> {isAuditing ? 'Auditing...' : 'Run Full Audit'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Overall Health & Tips */}
        <div className="space-y-8">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-center">
            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6">Profile Health Score</h3>
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-48 h-48 transform -rotate-90">
                <circle cx="96" cy="96" r="88" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100" />
                <motion.circle 
                  cx="96" cy="96" r="88" stroke="currentColor" strokeWidth="12" fill="transparent" 
                  strokeDasharray={552.92}
                  initial={{ strokeDashoffset: 552.92 }}
                  animate={{ strokeDashoffset: 552.92 - (552.92 * overallScore) / 100 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="text-blue-600" 
                />
              </svg>
              <div className="absolute flex flex-col items-center">
                <span className="text-5xl font-black text-gray-900">{overallScore}%</span>
                <span className="text-xs font-bold text-blue-600 mt-1">B+ Rating</span>
              </div>
            </div>
            <p className="mt-8 text-gray-600 text-sm leading-relaxed">
              Your profile is stronger than <span className="text-gray-900 font-bold">82%</span> of candidates in your field.
            </p>
          </div>

          <div className="bg-blue-900 p-8 rounded-3xl text-white shadow-xl">
            <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
              <SafeIcon icon={FiLightbulb} className="text-yellow-400" /> Pro Optimization Tips
            </h3>
            <div className="space-y-6">
              {tips.slice(0, showAllTips ? tips.length : 2).map((tip) => (
                <div key={tip.id} className="group cursor-default">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-black uppercase text-blue-300 tracking-wider">[{tip.category}]</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-black uppercase ${tip.impact === 'High' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>
                      {tip.impact} Impact
                    </span>
                  </div>
                  <h4 className="font-bold text-sm mb-1 group-hover:text-blue-300 transition-colors uppercase">{tip.title}</h4>
                  <p className="text-xs text-blue-100/70 leading-relaxed">{tip.description}</p>
                </div>
              ))}
            </div>
            {tips.length > 2 && (
              <button onClick={() => setShowAllTips(!showAllTips)} className="w-full mt-8 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2">
                {showAllTips ? 'Show Less' : `View All ${tips.length} Tips`} <SafeIcon icon={FiChevronRight} className={`transition-transform ${showAllTips ? '-rotate-90' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Right Column: Detailed Tasks */}
        <div className="lg:col-span-2 space-y-6">
          {sections.map((section, idx) => (
            <motion.div 
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden"
            >
              <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  {section.id === 'headline' ? <SafeIcon icon={FiUser} className="text-blue-500" /> : 
                   section.id === 'social' ? <SafeIcon icon={FiGlobe} className="text-purple-500" /> : 
                   <SafeIcon icon={FiTarget} className="text-orange-500" />}
                  {section.name}
                </h3>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400">
                  Efficiency Score: <span className={section.score > 70 ? 'text-green-600' : 'text-orange-600'}>{section.score || section.matchRate}%</span>
                </div>
              </div>

              <div className="p-8">
                {section.id === 'headline' && (
                  <div className="space-y-6">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Current Headline</p>
                      <p className="text-gray-600 italic bg-gray-50 p-4 rounded-xl border border-gray-100">"{section.current}"</p>
                    </div>
                    {section.current === section.aiOptimized ? (
                      <div className="p-6 bg-green-50 border border-green-100 rounded-2xl flex items-center gap-3">
                        <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
                        <div>
                          <p className="text-sm font-black text-green-900 uppercase">Fully Optimized!</p>
                          <p className="text-xs text-green-700">Your current headline meets all industry standards.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6 bg-blue-50 border border-blue-100 rounded-2xl">
                        <div className="flex justify-between items-start mb-4">
                          <p className="text-[10px] font-black text-blue-600 uppercase">AI Recommended</p>
                          <div className="flex gap-4">
                            <button onClick={() => applyHeadline(section.aiOptimized)} className="text-xs font-black text-blue-600 hover:underline">Apply to Profile</button>
                            <button onClick={() => handleCopy(section.aiOptimized)} className="text-xs font-black text-blue-600 hover:underline">
                              {copied ? 'Copied!' : 'Copy to Clipboard'}
                            </button>
                          </div>
                        </div>
                        <p className="text-lg font-black text-blue-900 leading-tight">"{section.aiOptimized}"</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-3 text-center">Optimization Rationale</p>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {section.suggestions.map((s, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                            <SafeIcon icon={FiCheckCircle} className="text-green-500 shrink-0" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {section.id === 'social' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {section.links.map((link, idx) => (
                      <div key={`${link.platform}-${idx}`} className="p-4 rounded-2xl border border-gray-100 hover:border-blue-200 transition-all group">
                        <div className="flex justify-between items-start mb-3">
                          <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                            <SafeIcon icon={['LinkedIn', 'Twitter', 'Facebook'].includes(link.platform) ? FiLinkedin : ['GitHub', 'Bitbucket'].includes(link.platform) ? FiGithub : FiGlobe} className="text-xl text-gray-400 group-hover:text-blue-600" />
                          </div>
                          {getStatusIcon(link.status)}
                        </div>
                        <h4 className="font-bold text-gray-900">{link.platform}</h4>
                        <p className="text-[10px] text-gray-500 truncate mt-0.5">{link.url || 'Not Connected'}</p>
                        <button className="mt-4 w-full py-2 bg-gray-50 text-[10px] font-black text-gray-500 rounded-lg hover:bg-blue-600 hover:text-white transition-all uppercase tracking-wider">
                          {link.status === 'Missing' ? 'Connect Now' : 'Re-verify'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {section.id === 'market' && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-8">
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Role Match Rate</p>
                        <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                          <div className="bg-orange-500 h-full w-[82%]"></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-black text-gray-900">82%</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-3">Top Market Trends You Should Adopt</p>
                      <div className="flex flex-wrap gap-2">
                        {section.topKeywords.map(k => (
                          <span key={k} className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-xl text-xs font-bold border border-gray-100 hover:border-blue-200 cursor-default">
                            {k}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileOptimizer;